import {
  type ComponentRef,
  createElement,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  type LayoutChangeEvent,
  type LayoutRectangle,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  type StyleProp,
  Text,
  useWindowDimensions,
  View,
  type View as ViewRef,
  type ViewStyle,
} from 'react-native';
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { StyleSheet } from 'react-native-unistyles';
import { scheduleOnRN } from 'react-native-worklets';
import Icon from '@/components/Unistyles/Icon';
import useHaptics from '@/hooks/useHaptics';

type SelectOption = {
  label: string;
  value: string;
};

type WebSelectChangeEvent = {
  currentTarget: {
    value: string;
  };
};

type WebSelectNativeProps = {
  accessibilityLabel: string;
  disabled?: boolean;
  onValueChange: (value: string) => void;
  options: readonly SelectOption[];
  placeholder: string;
  selectedOption?: SelectOption;
  testID?: string;
};

export interface SelectProps {
  leadingAccessory?: ReactNode;
  accessibilityLabel?: string;
  disabled?: boolean;
  onValueChange?: (value: string) => void;
  options: readonly SelectOption[];
  placeholder?: string;
  testID?: string;
  value?: string | null;
  width?: ViewStyle['width'];
  style?: StyleProp<ViewStyle>;
}

type MenuPosition = {
  left: number;
  maxHeight: number;
  top: number;
  width?: ViewStyle['width'];
};

const SELECT_PLACEHOLDER_VALUE = '__1time_select_placeholder__';

export function SelectTrigger({
  leadingAccessory,
  accessibilityLabel,
  disabled,
  label,
  hasValue,
  width,
}: {
  label: string;
  hasValue: boolean;
} & Pick<
  SelectProps,
  'accessibilityLabel' | 'disabled' | 'width' | 'leadingAccessory'
>) {
  return (
    <View
      accessible
      accessibilityRole="combobox"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityState={{ disabled }}
      accessibilityValue={{ text: label }}
      style={[
        styles.root({ width }),
        styles.trigger({ disabled, open: false }),
        leadingAccessory ? styles.triggerWithAccessory : undefined,
      ]}
    >
      {leadingAccessory ? (
        <View style={styles.accessory}>{leadingAccessory}</View>
      ) : null}
      <Text numberOfLines={1} style={styles.value({ hasValue })}>
        {label}
      </Text>
      <Icon
        name="chevron-down-outline"
        size="md"
        variant="typographySecondary"
      />
    </View>
  );
}

export default function Select(props: SelectProps) {
  if (Platform.OS === 'web') {
    return <WebSelect {...props} />;
  }

  return <NativeSelect {...props} />;
}

function WebSelect({
  leadingAccessory,
  accessibilityLabel,
  disabled,
  onValueChange,
  options,
  placeholder = 'Select...',
  testID,
  value,
  width,
  style: extraStyles,
}: SelectProps) {
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const displayedValue = pendingValue ?? value;
  const selectedOption = options.find(
    (option) => option.value === displayedValue,
  );

  const onSelectOption = useHaptics((nextValue: string) => {
    setPendingValue(nextValue);
    onValueChange?.(nextValue);
  });

  useEffect(() => {
    if (pendingValue === null || pendingValue !== value) {
      return;
    }

    setPendingValue(null);
  }, [pendingValue, value]);

  return (
    <View style={[styles.root({ width }), extraStyles]}>
      <View
        style={[
          styles.trigger({ disabled, open: false }),
          styles.webTrigger,
          leadingAccessory ? styles.triggerWithAccessory : undefined,
        ]}
      >
        {leadingAccessory ? (
          <View style={styles.accessory}>{leadingAccessory}</View>
        ) : null}
        <Text
          numberOfLines={1}
          style={styles.value({ hasValue: Boolean(selectedOption) })}
        >
          {selectedOption?.label ?? placeholder}
        </Text>
        <Icon
          name="chevron-down-outline"
          size="md"
          variant="typographySecondary"
        />
        <WebSelectNative
          accessibilityLabel={accessibilityLabel ?? placeholder}
          disabled={disabled}
          onValueChange={onSelectOption}
          options={options}
          placeholder={placeholder}
          selectedOption={selectedOption}
          testID={testID}
        />
      </View>
    </View>
  );
}

function NativeSelect({
  leadingAccessory,
  accessibilityLabel,
  disabled,
  onValueChange,
  options,
  placeholder = 'Select...',
  testID,
  value,
  width,
  style: extraStyles,
}: SelectProps) {
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const displayedValue = pendingValue ?? value;
  const selectedOption = options.find(
    (option) => option.value === displayedValue,
  );
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [triggerLayout, setTriggerLayout] = useState<LayoutRectangle | null>(
    null,
  );
  const progress = useSharedValue(0);
  const hasScrolledToSelectedRef = useRef(false);
  const scrollRef = useRef<ComponentRef<typeof ScrollView>>(null);
  const triggerRef = useRef<ViewRef | null>(null);
  const windowDimensions = useWindowDimensions();

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: progress.value,
    transform: [
      { translateY: interpolate(progress.value, [0, 1], [-4, 0]) },
      { scaleY: interpolate(progress.value, [0, 1], [0.96, 1]) },
    ],
  }));

  const openSelect = () => {
    if (disabled) {
      return;
    }

    hasScrolledToSelectedRef.current = false;
    setTriggerLayout(null);
    const triggerNode = triggerRef.current;

    if (!triggerNode || typeof triggerNode.measureInWindow !== 'function') {
      setTriggerLayout({
        height: 0,
        width: typeof width === 'number' ? width : 0,
        x: 0,
        y: 0,
      });
      setOpen(true);
      return;
    }

    triggerNode.measureInWindow((x, y, measuredWidth, height) => {
      setTriggerLayout({
        height,
        width: measuredWidth,
        x,
        y,
      });
    });
    setOpen(true);
  };

  const closeSelect = () => {
    setOpen(false);
  };

  const onSelectOption = useHaptics((nextValue: string) => {
    setPendingValue(nextValue);
    onValueChange?.(nextValue);
    closeSelect();
  });

  const scrollToSelectedOption = (
    event: LayoutChangeEvent,
    viewportHeight: number,
  ) => {
    if (hasScrolledToSelectedRef.current) {
      return;
    }

    hasScrolledToSelectedRef.current = true;
    const { height: itemHeight, y: itemY } = event.nativeEvent.layout;
    const centeredY = Math.max(0, itemY - viewportHeight / 2 + itemHeight / 2);

    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({
        animated: false,
        y: centeredY,
      });
    });
  };

  useEffect(() => {
    if (pendingValue === null || pendingValue !== value) {
      return;
    }

    setPendingValue(null);
  }, [pendingValue, value]);

  useEffect(() => {
    if (open) {
      if (!mounted) {
        setMounted(true);
        progress.set(0);
      }
      return;
    }

    if (!mounted) {
      return;
    }

    progress.set(
      withTiming(
        0,
        { duration: 120, easing: Easing.out(Easing.cubic) },
        (finished) => {
          if (finished) {
            scheduleOnRN(setMounted, false);
          }
        },
      ),
    );
  }, [mounted, open, progress]);

  useEffect(() => {
    if (!open || !mounted || !triggerLayout) {
      return;
    }

    progress.set(
      withTiming(1, { duration: 160, easing: Easing.out(Easing.cubic) }),
    );
  }, [mounted, open, progress, triggerLayout]);

  useEffect(() => {
    if (!mounted) {
      return;
    }

    const node = scrollRef.current?.getScrollableNode?.();

    if (!isScrollableWheelTarget(node)) {
      return;
    }

    const onWheel = (event: WheelEvent) => {
      const maxScrollTop = node.scrollHeight - node.clientHeight;

      if (maxScrollTop <= 0) {
        return;
      }

      const nextScrollTop = Math.min(
        Math.max(node.scrollTop + event.deltaY, 0),
        maxScrollTop,
      );

      if (nextScrollTop === node.scrollTop) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      node.scrollTop = nextScrollTop;
    };

    node.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      node.removeEventListener('wheel', onWheel);
    };
  }, [mounted]);

  const menuPosition = getMenuPosition({
    fallbackWidth: width,
    optionCount: options.length,
    triggerLayout,
    windowHeight: windowDimensions.height,
    windowWidth: windowDimensions.width,
  });
  const canDisplayMenu = open && !!triggerLayout;

  return (
    <View style={[styles.root({ width }), extraStyles]}>
      <View ref={triggerRef} collapsable={false}>
        <Pressable
          accessibilityLabel={accessibilityLabel ?? placeholder}
          accessibilityRole="combobox"
          accessibilityState={{ disabled, expanded: open }}
          accessibilityValue={{
            text: selectedOption?.label ?? placeholder,
          }}
          disabled={disabled}
          onPress={openSelect}
          pointerEvents={disabled ? 'none' : 'auto'}
          testID={testID}
          style={({ pressed }) => [
            styles.trigger({ disabled, open }),
            leadingAccessory ? styles.triggerWithAccessory : undefined,
            pressed && styles.triggerPressed,
          ]}
        >
          {leadingAccessory ? (
            <View style={styles.accessory}>{leadingAccessory}</View>
          ) : null}
          <Text
            numberOfLines={1}
            style={styles.value({ hasValue: Boolean(selectedOption) })}
          >
            {selectedOption?.label ?? placeholder}
          </Text>
          <Icon
            name="chevron-down-outline"
            size="md"
            variant="typographySecondary"
          />
        </Pressable>
      </View>

      <Modal
        animationType="none"
        onRequestClose={closeSelect}
        presentationStyle="overFullScreen"
        transparent
        visible={mounted}
      >
        <View pointerEvents="box-none" style={styles.overlay}>
          <Pressable style={styles.backdrop} onPress={closeSelect} />
          <Animated.View
            pointerEvents={canDisplayMenu ? 'auto' : 'none'}
            style={[
              styles.content,
              styles.contentPosition(menuPosition),
              animatedStyle,
              !canDisplayMenu ? styles.hidden : null,
            ]}
          >
            <ScrollView
              keyboardShouldPersistTaps="handled"
              ref={scrollRef}
              showsVerticalScrollIndicator
              style={styles.viewport({ maxHeight: menuPosition.maxHeight })}
              contentContainerStyle={styles.viewportContent}
            >
              {options.map((option) => {
                const selected = option.value === displayedValue;

                return (
                  <Pressable
                    key={option.value}
                    onLayout={
                      selected
                        ? (event) =>
                            scrollToSelectedOption(
                              event,
                              menuPosition.maxHeight,
                            )
                        : undefined
                    }
                    onPress={() => onSelectOption(option.value)}
                    style={({ pressed }) => [
                      styles.item({ pressed, selected }),
                    ]}
                    testID={
                      testID ? `${testID}Option-${option.value}` : undefined
                    }
                  >
                    <Text numberOfLines={1} style={styles.itemText}>
                      {option.label}
                    </Text>
                    <View style={styles.itemIndicator}>
                      {selected ? (
                        <Icon
                          name="checkmark-outline"
                          size="md"
                          variant="primary"
                        />
                      ) : null}
                    </View>
                  </Pressable>
                );
              })}
            </ScrollView>
          </Animated.View>
        </View>
      </Modal>
    </View>
  );
}

function WebSelectNative({
  accessibilityLabel,
  disabled,
  onValueChange,
  options,
  placeholder,
  selectedOption,
  testID,
}: WebSelectNativeProps): ReactNode {
  const onChange = (event: WebSelectChangeEvent) => {
    const nextValue = event.currentTarget.value;

    if (nextValue === SELECT_PLACEHOLDER_VALUE) {
      return;
    }

    onValueChange(nextValue);
  };

  return createElement(
    'select',
    {
      'aria-label': accessibilityLabel,
      'data-testid': testID,
      disabled,
      onChange,
      style: {
        appearance: 'auto',
        backgroundColor: 'transparent',
        border: 0,
        bottom: 0,
        cursor: disabled ? 'default' : 'pointer',
        height: '100%',
        left: 0,
        margin: 0,
        opacity: 0,
        padding: 0,
        position: 'absolute',
        right: 0,
        top: 0,
        width: '100%',
      },
      value: selectedOption?.value ?? SELECT_PLACEHOLDER_VALUE,
    },
    [
      createElement(
        'option',
        {
          disabled: true,
          hidden: true,
          key: SELECT_PLACEHOLDER_VALUE,
          value: SELECT_PLACEHOLDER_VALUE,
        },
        placeholder,
      ),
      ...options.map((option) =>
        createElement(
          'option',
          {
            'data-testid': testID
              ? `${testID}Option-${option.value}`
              : undefined,
            key: option.value,
            value: option.value,
          },
          option.label,
        ),
      ),
    ],
  );
}

type ScrollableWheelTarget = {
  addEventListener(
    type: 'wheel',
    listener: (event: WheelEvent) => void,
    options?: AddEventListenerOptions,
  ): void;
  clientHeight: number;
  removeEventListener(
    type: 'wheel',
    listener: (event: WheelEvent) => void,
  ): void;
  scrollHeight: number;
  scrollTop: number;
};

function isScrollableWheelTarget(
  value: unknown,
): value is ScrollableWheelTarget {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return (
    typeof Reflect.get(value, 'addEventListener') === 'function' &&
    typeof Reflect.get(value, 'removeEventListener') === 'function' &&
    typeof Reflect.get(value, 'clientHeight') === 'number' &&
    typeof Reflect.get(value, 'scrollHeight') === 'number' &&
    typeof Reflect.get(value, 'scrollTop') === 'number'
  );
}

function getMenuPosition({
  fallbackWidth,
  optionCount,
  triggerLayout,
  windowHeight,
  windowWidth,
}: {
  fallbackWidth?: ViewStyle['width'];
  optionCount: number;
  triggerLayout: LayoutRectangle | null;
  windowHeight: number;
  windowWidth: number;
}): MenuPosition {
  const gap = 4;
  const estimatedItemHeight = 40;
  const estimatedViewportPadding = 4;
  const viewportMargin = 8;
  const fallbackMenuWidth =
    typeof fallbackWidth === 'number' ? fallbackWidth : undefined;
  const menuWidth = triggerLayout?.width ?? fallbackMenuWidth;
  const rawLeft = triggerLayout?.x ?? viewportMargin;
  const maxLeft =
    menuWidth === undefined
      ? rawLeft
      : Math.max(viewportMargin, windowWidth - menuWidth - viewportMargin);
  const left = Math.min(Math.max(rawLeft, viewportMargin), maxLeft);
  const top = triggerLayout
    ? triggerLayout.y + triggerLayout.height + gap
    : viewportMargin;
  const availableBelow = windowHeight - top - viewportMargin;
  const availableAbove = triggerLayout
    ? triggerLayout.y - viewportMargin - gap
    : 0;
  const shouldOpenAbove =
    availableBelow < 160 && availableAbove > availableBelow;
  const maxHeight = Math.max(
    80,
    Math.min(224, shouldOpenAbove ? availableAbove : availableBelow),
  );
  const estimatedMenuHeight = Math.min(
    maxHeight,
    optionCount * estimatedItemHeight + estimatedViewportPadding,
  );
  const resolvedTop =
    shouldOpenAbove && triggerLayout
      ? Math.max(viewportMargin, triggerLayout.y - gap - estimatedMenuHeight)
      : top;

  return {
    left,
    maxHeight,
    top: resolvedTop,
    width: triggerLayout?.width ?? fallbackWidth,
  };
}

const styles = StyleSheet.create((theme) => ({
  accessory: {
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  triggerWithAccessory: {
    gap: theme.space('lg'),
    paddingHorizontal: theme.space('lg'),
    paddingVertical: theme.space('lg'),
  },
  backdrop: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  content: {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.borderMuted,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    elevation: 8,
    minWidth: theme.size(40),
    overflow: 'hidden',
    position: 'absolute',
    shadowColor: theme.colors.black,
    shadowOffset: {
      height: 8,
      width: 0,
    },
    shadowOpacity: 0.18,
    shadowRadius: 16,
    zIndex: 1000,
    _web: {
      boxShadow: '0 12px 28px rgba(0, 0, 0, 0.18)',
      zIndex: 1000,
    },
  },
  contentPosition: ({ left, top, width }: MenuPosition) => ({
    left,
    top,
    width,
  }),
  hidden: {
    opacity: 0,
  },
  item: ({ pressed, selected }: { pressed: boolean; selected: boolean }) => ({
    alignItems: 'center',
    backgroundColor: pressed
      ? theme.colors.surfaceMuted
      : selected
        ? theme.colors.surfaceSecondary
        : theme.colors.surface,
    flexDirection: 'row',
    gap: theme.space('md'),
    justifyContent: 'space-between',
    minHeight: theme.size(10),
    paddingHorizontal: theme.space('md'),
    _web: {
      _hover: {
        backgroundColor: theme.colors.surfaceMuted,
      },
    },
  }),
  itemIndicator: {
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.size(5),
  },
  itemText: {
    color: theme.colors.typography,
    flex: 1,
    fontSize: theme.fontSize.md,
    fontWeight: theme.fontWeight.medium,
  },
  overlay: {
    flex: 1,
  },
  root: ({ width }: Pick<SelectProps, 'width'>) => ({
    width,
  }),
  trigger: ({
    disabled,
    open,
  }: Pick<SelectProps, 'disabled'> & { open: boolean }) => ({
    alignItems: 'center',
    backgroundColor: theme.colors.surface,
    borderColor: open ? theme.colors.borderSecondary : theme.colors.borderMuted,
    borderRadius: theme.borderRadius.lg,
    borderWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    gap: theme.space('sm'),
    minHeight: theme.size(10),
    opacity: disabled ? 0.5 : 1,
    paddingHorizontal: theme.space('md'),
    _web: {
      cursor: disabled ? 'default' : 'pointer',
      _hover: {
        borderColor: theme.colors.borderSecondary,
      },
    },
  }),
  triggerPressed: {
    backgroundColor: theme.colors.surfaceSecondary,
  },
  value: ({ hasValue }: { hasValue: boolean }) => ({
    color: hasValue ? theme.colors.typography : theme.colors.typographyMuted,
    flex: 1,
    fontSize: theme.fontSize.md,
  }),
  viewport: ({ maxHeight }: Pick<MenuPosition, 'maxHeight'>) => ({
    maxHeight,
  }),
  viewportContent: {
    paddingVertical: theme.space('xs'),
  },
  webTrigger: {
    flexGrow: 1,
    overflow: 'hidden',
    position: 'relative',
    _web: {
      _focusWithin: {
        borderColor: theme.colors.borderSecondary,
      },
    },
  },
}));
